import React, { useEffect, useState } from "react";
import {
  FaUser,
  FaUsers,
  FaBuilding,
  FaIdBadge,
  FaBell,
  FaCog,
  FaSignOutAlt,
  FaEnvelopeOpenText,
  FaChartLine,
  FaRobot,
  FaCreditCard,
  FaChartBar,
  FaUserPlus,
  FaCrown,
  FaHeadset,
  FaGamepad,
  FaChevronDown,
  FaChevronRight,
  FaChevronLeft,
} from "react-icons/fa";
import Logo from "@/assets/Pitchster-logo.png";
import LogoFinal from "@/assets/Pitchster-logo-final.png";
import { MdSpaceDashboard } from "react-icons/md";
import { HiDocumentText } from "react-icons/hi";
import { HiBuildingOffice2 } from "react-icons/hi2";
import { BsChatDotsFill } from "react-icons/bs";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import useAuthStore from "@/store/useAuthStore";
import { useUserStore } from "@/store";
import CustomLogoutDialog from "../Popup/CustomLogoutDialog";
import useSidebarStore from "@/store/useSidebarStore";
import useThemeStore from "@/store/useThemeStore"; // Import useThemeStore
import { useRequest } from "@/hooks/useAdminDashboardService";
import { useTenantById } from "@/hooks/useTenantDashboardService";
import { canViewMeetingDashboard, canViewCompanyProfile, canViewBilling, canViewDigitalTwins, canViewPracticeEngine, canViewOrganizations, canViewAdminRequests, canViewSubscription } from '@/config/permissions';

interface SidebarItem {
  name: string;
  icon: React.ReactNode;
  navigateTo: string;
  disabled?: boolean;
  badge?: React.ReactNode;
  show: boolean;
}

interface SidebarSection {
  title?: string;
  icon?: React.ReactNode;
  items: SidebarItem[];
}

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logoutUser } = useAuth();
  const { role } = useAuthStore();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const { setPageHeader } = useUserStore();
  const { isSidebarOpen, setSidebarOpen, isCollapsed, toggleCollapsed } = useSidebarStore();
  const { theme } = useThemeStore(); // Get theme from store
  const isDark = theme === "dark";

  const { data: upgradeData } = useRequest({
    page: 1,
    pageSize: 1,
    requestContext: "UPGRADE",
    status: "PENDING",
  });

  const pendingUpgradeCount =
    (upgradeData as any)?.data?.totalCount ||
    (upgradeData as any)?.data?.total ||
    0;

  const normalizedRole = (role || "").toUpperCase();
  const isAdmin = normalizedRole === "ADMIN";
  const isManager = normalizedRole === "MANAGER";
  const isSuperAdmin = normalizedRole === "SUPER_ADMIN";
  const isAdminOrManagerOrSuperAdmin = isAdmin || isManager || isSuperAdmin;
  const isAdminOrSuperAdmin = isAdmin || isSuperAdmin;

  const sections: SidebarSection[] = [
    {
      items: [
        {
          name: "Dashboard",
          icon: <MdSpaceDashboard className="text-lg" />,
          navigateTo: "/dashboard",
          show: true,
        },
      ],
    },
    {
      title: "User Management",
      icon: <FaUsers className="text-base" />,
      items: [
        {
          name: "Users",
          icon: <FaUser className="text-sm" />,
          navigateTo: "/users",
          show: isAdminOrManagerOrSuperAdmin,
        },
        {
          name: "Organizations",
          icon: <HiBuildingOffice2 className="text-base" />,
          navigateTo: "/tenants",
          show: isSuperAdmin && canViewOrganizations(role),
        },
        {
          name: "Grant Credits",
          icon: <FaBuilding className="text-sm" />,
          navigateTo: "/grant-credits",
          show: isSuperAdmin || isAdmin,
        },
        {
          name: "My Organization",
          icon: <FaBuilding className="text-sm" />,
          navigateTo: "/company-profile-info",
          show: isAdminOrManagerOrSuperAdmin && canViewCompanyProfile(role),
        },
        {
          name: "Teams",
          icon: <FaIdBadge className="text-sm" />,
          navigateTo: "/teams",
          show: isAdminOrManagerOrSuperAdmin,
        },
      ],
    },
    {
      title: "Sales Performance",
      icon: <FaChartLine className="text-base" />,
      items: [
        {
          name: "Activity Dashboard",
          icon: <BsChatDotsFill className="text-sm" />,
          navigateTo: "/meeting-dashboard",
          show: isAdminOrManagerOrSuperAdmin && canViewMeetingDashboard(role),
        },
        {
          name: "Guided Practice Scenarios",
          icon: <FaGamepad className="text-sm" />,
          navigateTo: "/practice-engine",
          show: isAdminOrManagerOrSuperAdmin && canViewPracticeEngine(role),
        },
        {
          name: "Scripts",
          icon: <HiDocumentText className="text-base" />,
          navigateTo: "/script",
          show: isAdminOrManagerOrSuperAdmin && canViewPracticeEngine(role),
        },
        {
          name: "Digital Twin Templates",
          icon: <FaRobot className="text-sm" />,
          navigateTo: "/digital-twin-templates",
          show: isAdminOrSuperAdmin && canViewDigitalTwins(role),
        },
      ],
    },
    {
      title: "Analytics",
      icon: <FaChartBar className="text-base" />,
      items: [
        {
          name: "Credit Usage",
          icon: <FaCreditCard className="text-base" />,
          navigateTo: "/credit-usage",
          show: isAdminOrSuperAdmin && canViewBilling(role),
        },
      ],
    },
    {
      title: "Administration",
      icon: <FaCog className="text-base" />,
      items: [
        {
          name: "Account Requests",
          icon: <FaUserPlus className="text-sm" />,
          navigateTo: "/admin/requests",
          show: isSuperAdmin && canViewAdminRequests(role),
        },
        {
          name: "Contact Requests",
          icon: <FaEnvelopeOpenText className="text-sm" />,
          navigateTo: "/contact-requests",
          badge: pendingUpgradeCount > 0 ? (
            <span className="ml-auto bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] flex items-center justify-center">
              {pendingUpgradeCount}
            </span>
          ) : null,
          show: isSuperAdmin && canViewAdminRequests(role),
        },
        {
          name: "Notifications",
          icon: <FaBell className="text-sm" />,
          navigateTo: "/notification",
          show: true,
        },
        {
          name: "Subscription Plan",
          icon: <FaCrown className="text-sm" />,
          navigateTo: "/subscription",
          show: isSuperAdmin && canViewSubscription(role),
        },
        {
          name: "Settings",
          icon: <FaCog className="text-sm" />,
          navigateTo: "/settings",
          show: true,
        },
      ],
    },
    {
      title: "Support",
      icon: <FaHeadset className="text-base" />,
      items: [
        {
          name: "Support",
          icon: <FaHeadset className="text-sm" />,
          navigateTo: "/contact",
          show: true,
        },
      ],
    },
  ];

  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
    "User Management": true,
    "Sales Performance": true,
    "Analytics": true,
    "Administration": true,
    "Support": true,
  });

  const toggleCategory = (title: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const [selectedPath, setSelectedPath] = useState("/dashboard");

  useEffect(() => {
    if (!location?.pathname) return;
    const currentPath = location.pathname;

    const allItems = sections.flatMap((sec) => sec.items.filter((item) => item.show));
    const matched = allItems.find((item) => {
      if (!item || !item.navigateTo) return false;
      return currentPath === item.navigateTo || currentPath.startsWith(item.navigateTo + "/");
    });

    let activeRoute = "/dashboard";
    if (matched?.navigateTo) {
      activeRoute = matched.navigateTo;
    } else if (currentPath.startsWith("/company-profile-manual") || currentPath.startsWith("/company-profile")) {
      activeRoute = "/company-profile-info";
    } else if (currentPath.startsWith("/meeting-type-list") || currentPath.startsWith("/meeting-detail")) {
      activeRoute = "/meeting-dashboard";
    } else if (currentPath.startsWith("/script-details") || currentPath.startsWith("/script")) {
      activeRoute = "/script";
    } else if (currentPath.startsWith("/digital-twin")) {
      activeRoute = "/digital-twin-templates";
    }

    setSelectedPath(activeRoute);

    sections.forEach((sec) => {
      if (sec.title && sec.items.some((item) => item.navigateTo === activeRoute)) {
        setExpandedCategories((prev) => ({ ...prev, [sec.title!]: true }));
      }
    });
  }, [location.pathname]);

  const handleNavigation = (
    navigateTo: string,
    name: string,
    disabled: boolean
  ) => {
    if (disabled) return;
    if (name === "Dashboard") {
      setPageHeader(
        role === "ADMIN"
          ? "Admin Dashboard"
          : role === "SUPER_ADMIN"
            ? "Super Admin Dashboard"
            : role === "MANAGER"
              ? "Manager"
              : ""
      );
    } else if (name === "Users") {
      setPageHeader("User Management");
    } else if (name === "Activity Dashboard" || name === "Meeting Dashboard") {
      setPageHeader("Activity Dashboard");
    } else {
      setPageHeader(name);
    }
    setSelectedPath(navigateTo);
    navigate(navigateTo);
    setSidebarOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logoutUser();
    } catch (error) {
      console.error("Logout failed", error);
    } finally {
      useAuthStore.getState().logout();
      useUserStore.getState().reset();
      localStorage.clear();
      window.location.href = "/login";
    }
  };

  const tenantId = localStorage.getItem("tenantId") || useAuthStore((state) => state.tenantid) || "";
  const { data: tenantData } = useTenantById({
    tenantId,
    query: {},
  });

  const getLogoUrl = (url?: string | null) => {
    if (!url) return theme === "light" ? LogoFinal : Logo;
    if (url.startsWith("http")) return url;
    
    const imageBase = import.meta.env.VITE_ENV_API_BASEURL_image || 
                      `${import.meta.env.VITE_ENV_API_BASEURL || "http://localhost:5001"}/uploads`;
    
    const baseWithSlash = imageBase.endsWith("/") ? imageBase : `${imageBase}/`;
    return `${baseWithSlash}${url}`;
  };

  const hasCustomLogo = !!tenantData?.logoUrl;

  return (
    <>
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-10 md:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      <aside
        className={`fixed top-0 left-0 z-20 h-screen bg-sidebar p-4 flex flex-col justify-between transition-all duration-300 ease-in-out shadow-sm
          ${isCollapsed ? "w-20" : "w-64"}
          ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0`}
      >
        <div className="flex items-center justify-between w-full mb-3 mt-1 flex-shrink-0 px-1">
          {!isCollapsed ? (
            <div className="flex flex-col items-center justify-center flex-1">
              <div className="flex items-center justify-center h-[40px] w-full">
                <img
                  src={getLogoUrl(tenantData?.logoUrl)}
                  alt="Logo"
                  className="h-full object-contain"
                />
              </div>
              {hasCustomLogo && (
                <span className="text-gray-400 text-xs mt-1 italic font-light">
                  Powered by Pitchster
                </span>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-[36px] w-full">
              <img
                src={getLogoUrl(tenantData?.logoUrl)}
                alt="Logo"
                className="h-full object-contain max-w-[36px]"
              />
            </div>
          )}

          <button
            onClick={toggleCollapsed}
            title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            className={`hidden md:flex items-center justify-center p-1.5 rounded-lg transition-colors ml-1 flex-shrink-0 ${
              isDark
                ? "text-gray-400 hover:text-white hover:bg-[#1c1b23]"
                : "text-gray-600 hover:text-black hover:bg-gray-200"
            }`}
          >
            {isCollapsed ? (
              <FaChevronRight className="text-sm" />
            ) : (
              <FaChevronLeft className="text-sm" />
            )}
          </button>
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto custom-scrollbar thin-scrollbar space-y-3 pr-1">
          {sections.map((section, idx) => {
            const visibleItems = section.items.filter((item) => item.show);
            if (visibleItems.length === 0) return null;

            const isExpanded = section.title ? expandedCategories[section.title] : true;

            return (
              <div key={idx} className="space-y-1">
                {section.title && !isCollapsed && (
                  <div
                    onClick={() => toggleCategory(section.title!)}
                    className={`flex items-center justify-between px-3 py-2 font-semibold text-sm rounded-lg cursor-pointer select-none transition-colors duration-150 ${
                      isDark
                        ? "text-white hover:bg-[#1c1b23]"
                        : "text-[#14131A] hover:bg-gray-200/60"
                    }`}
                  >
                    <div className="flex items-center space-x-2.5">
                      <span className="text-base flex-shrink-0">{section.icon}</span>
                      <span className="tracking-wide truncate">{section.title}</span>
                    </div>
                    <span className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      {isExpanded ? (
                        <FaChevronDown className="text-xs" />
                      ) : (
                        <FaChevronRight className="text-xs" />
                      )}
                    </span>
                  </div>
                )}

                {section.title && isCollapsed && (
                  <div className={`my-2 border-t ${isDark ? "border-white/10" : "border-gray-300"}`} title={section.title} />
                )}
                
                {(isExpanded || isCollapsed) && (
                  <div className={`space-y-1 ${section.title && !isCollapsed ? "pl-3 mt-1" : ""}`}>
                    {visibleItems.map((item) => {
                      const isSelected = selectedPath === item.navigateTo;
                      return (
                        <div
                          key={item.navigateTo}
                          title={isCollapsed ? item.name : undefined}
                          onClick={() =>
                            handleNavigation(
                              item.navigateTo,
                              item.name,
                              item.disabled || false
                            )
                          }
                          className={`
                            flex items-center text-sm font-semibold rounded-lg cursor-pointer transition-all duration-150
                            ${isCollapsed ? "justify-center px-0 py-2.5" : "px-3 py-2 space-x-2.5"}
                            ${
                              item.disabled
                                ? "text-gray-400 cursor-not-allowed opacity-60"
                                : isSelected
                                ? isDark
                                  ? "bg-[#501b99] text-white shadow-sm font-bold"
                                  : "bg-[#5a9d50] text-white shadow-md font-bold"
                                : isDark
                                ? "text-white/80 hover:bg-[#1c1b23] hover:text-white"
                                : "text-[#14131A] hover:bg-gray-200/80 hover:text-black"
                            }
                          `}
                        >
                          <span className={`flex-shrink-0 ${isCollapsed ? "text-xl" : "text-base"}`}>
                            {item.icon}
                          </span>
                          {!isCollapsed && <span className="truncate">{item.name}</span>}
                          {!isCollapsed && item.badge}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className={`flex-shrink-0 pt-2 pb-1 border-t ${isDark ? "border-white/10" : "border-gray-200"} mt-2`}>
          <div
            onClick={() => setShowLogoutConfirm(true)}
            title={isCollapsed ? "Logout" : undefined}
            className={`flex items-center text-sm font-semibold rounded-lg cursor-pointer transition-all duration-150
              ${isCollapsed ? "justify-center px-0 py-2.5" : "px-3 py-2 space-x-2.5"}
              ${isDark ? "text-red-400 hover:bg-[#1c1b23]" : "text-red-600 hover:bg-red-50"}`}
          >
            <FaSignOutAlt className={`text-red-500 ${isCollapsed ? "text-xl" : "text-base"}`} />
            {!isCollapsed && <span>Logout</span>}
          </div>
        </div>
      </aside>

      {showLogoutConfirm && (
        <CustomLogoutDialog
          open={showLogoutConfirm}
          onClose={() => setShowLogoutConfirm(false)}
          onConfirm={handleLogout}
        />
      )}
    </>
  );
};

export default Sidebar;
